import { gql } from '@apollo/client'
import { BodyText, Button, Caption, Heading, Separator } from '@farol-ds/react'
import { Price } from '@jusbrasil-web/shared-price'
import { Fragment } from 'react'

import { BenefitsList } from '../benefits-list'
import { CheckoutButton } from '../checkout-button'
import { OneClickButton } from '../one-click-button'
import {
  DoctrineOneOfferLockLoggedContent_OfferLockDataFragment,
  DoctrineOneOfferLockLoggedContent_ViewerFragment,
} from './__generated__/doctrine-one-offer-lock-logged-content.graphql'
import styles from './doctrine-one-offer-lock-logged-content.module.scss'

export interface DoctrineOneOfferLockLoggedContentProps {
  offerLockData: DoctrineOneOfferLockLoggedContent_OfferLockDataFragment
  viewer: DoctrineOneOfferLockLoggedContent_ViewerFragment
}

export function DoctrineOneOfferLockLoggedContent({
  offerLockData,
  viewer,
}: DoctrineOneOfferLockLoggedContentProps) {
  const { body, header, footer } = offerLockData || {}
  const { title = '', subtitle = '' } = header || {}
  const {
    cards: [card],
  } = body || {}

  const isPromotional = !!card.offer.priceAfterPromotionalPeriodText

  return (
    <>
      <div className={styles.header}>
        <Heading size="lg" className={styles.title}>
          <span dangerouslySetInnerHTML={{ __html: title }} />
        </Heading>
        <BodyText size="md" className={styles.subtitle}>
          <span dangerouslySetInnerHTML={{ __html: subtitle }} />
        </BodyText>
      </div>

      <div className={styles.card}>
        <div className={styles.wrapperPrice}>
          <Price align="center" size="lg">
            {isPromotional ? (
              <Price.Discount>
                de{' '}
                {card.offer.installmentsText && (
                  <strong className={styles['card-price-installments']}>
                    {card.offer.installmentsText}
                  </strong>
                )}{' '}
                <Price.DiscountStrike>
                  {card.offer.priceAfterPromotionalPeriodText}
                </Price.DiscountStrike>{' '}
                por:
              </Price.Discount>
            ) : (
              <Price.Discount>
                {card.offer.installmentsText ? (
                  <span>
                    Por apenas{' '}
                    <strong className={styles['card-price-installments']}>
                      {card.offer.installmentsText}
                    </strong>{' '}
                    de:
                  </span>
                ) : (
                  card.offer.priceTitle && (
                    <Price.Title>
                      <span dangerouslySetInnerHTML={{ __html: card.offer.priceTitle }} />
                    </Price.Title>
                  )
                )}
              </Price.Discount>
            )}

            <Price.Value>
              {card.offer.priceText}
              {card.offer.frequency && !card.offer.frequencyPeriodText && (
                <Price.ValueFrequency>{card.offer.frequency}</Price.ValueFrequency>
              )}
            </Price.Value>
            {card.offer.frequencyPeriodText && (
              <Price.Helptext>no {card.offer.frequencyPeriodText}</Price.Helptext>
            )}
          </Price>

          <CheckoutButton offerLockCard={card} kind="primary" />
          <OneClickButton offerLockCard={card} viewer={viewer} kind="primary" />
        </div>

        <BenefitsList offerLockCard={card} benefitsListClassName={styles.benefits} />
      </div>

      {footer && (footer.text || !!footer.links.length) && (
        <div className={styles.footer}>
          {footer.text && <Caption className={styles.text}>{footer.text}</Caption>}

          {!!footer.links.length && (
            <div className={styles.links}>
              {footer.links.map(({ label, url }, index) => (
                <Fragment key={label + url}>
                  <Button kind="plain" asChild>
                    <a href={url} target="_blank" rel="noreferrer">
                      {label}
                    </a>
                  </Button>

                  {index < footer.links.length - 1 && <Separator orientation="vertical" />}
                </Fragment>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  )
}

DoctrineOneOfferLockLoggedContent.fragments = {
  offerLockData: gql`
    fragment DoctrineOneOfferLockLoggedContent_offerLockData on OfferLockData {
      header {
        title
        subtitle
      }
      body {
        cards {
          offer {
            priceTitle
            priceText
            frequency
            priceAfterPromotionalPeriodText
            installmentsText
            frequencyPeriodText
          }
          ...OneClickButton_offerLockCard
          ...CheckoutButton_offerLockCard
          ...BenefitsList_offerLockCard
        }
      }
      footer {
        text
        links {
          label
          url
        }
      }
    }
    ${OneClickButton.fragments.offerLockCard}
    ${CheckoutButton.fragments.offerLockCard}
    ${BenefitsList.fragments.offerLockCard}
  `,
  viewer: gql`
    fragment DoctrineOneOfferLockLoggedContent_viewer on Profile {
      ...OneClickButton_viewer
    }
    ${OneClickButton.fragments.viewer}
  `,
}
